import { useContext, useEffect, useState } from "react";
import { TypeParams } from "../../types";
import EgwClientareaHome from "./Home";
import EgwAuthenticationForm1Login from "./Authentication/Form1Login";
import { Alert, Breadcrumb, Spin } from "antd";
import { GlobalStateContext } from "../../lib/GlobalContext";
import EgwClientareaBestellung from "./ProdukteBestellung";
import EgwClientareaDokumente from "./EgwDokumente";
import EgwClientareaSleepEvaluation from "./EgwSleepEvaluation";
import EgwClientareaSurveys from "./Survey/EgwSurveys";
import EgwAuthenticationForm1Resend from "./Authentication/Form1Resend";
import EgwKontrollterminRegistration from "./KontrollterminRegistration/KontrollterminRegistration";
import { useTranslation } from "react-i18next";
import EgwUnsubscribe from "./Unsubscribe";
import EgwSurveyForm from "./Survey/EgwSurveyForm";
import EgwDisclaimerForm from "./EgwDisclaimerForm";

interface EgwClientareaProps {
  params: TypeParams;
}

const getSelectedStyle = (params: any, section: string) => {
  return {
    color: params.section === section ? "black" : "inherit",
    fontWeight: params.section === section ? "bold" : "normal",
  };
};

const EgwClientarea: React.FC<EgwClientareaProps> = ({ params }) => {
  const { state, logout, navigate, checkAuth, isPageForbidden, isRedirectedLogin, isLoginPage, isLogoutPage, isClientNumberPage } =
    useContext(GlobalStateContext);
  const [preload, setPreload] = useState(false);
  const { t } = useTranslation();
  const [menu, setMenu] = useState<JSX.Element>(<></>);

  if (!params) {
    throw new Error("No params provided");
  }

  useEffect(() => {
    if (!isLogoutPage()) {
      checkAuth().then(() => {
        setPreload(true);
      });
    }
  }, [checkAuth, isLogoutPage]);

  useEffect(() => {
    if (state.params?.showMenu) {
      setMenu(
        <div
          style={{
            width: "100%",
            whiteSpace: "nowrap",
            marginBottom: "3em",
            marginTop: "0px",
          }}
        >
          <Breadcrumb
            style={{
              whiteSpace: "nowrap",
            }}
            separator="|"
            items={[
              {
                title: (
                  <span className="submenu" style={getSelectedStyle(params, "home")} onClick={() => navigate(params, "home")}>
                    {t("kundenbereich.menu.home")}
                  </span>
                ),
              },
              {
                title: (
                  <span
                    className="submenu"
                    style={getSelectedStyle(params, "schlafqualitaet")}
                    onClick={() => navigate(params, "schlafqualitaet")}
                  >
                    {t("kundenbereich.menu.schlafqualitaet")}
                  </span>
                ),
              },
              // {
              //   title: (
              //     <span className="submenu" style={getSelectedStyle(params,"befragungen")} onClick={() => navigate(params, "befragungen")}>
              //       {t("kundenbereich.menu.befragungen")}
              //     </span>
              //   ),
              // },
              {
                title: (
                  <span className="submenu" style={getSelectedStyle(params, "dokumente")} onClick={() => navigate(params, "dokumente")}>
                    {t("kundenbereich.menu.dokumente")}
                  </span>
                ),
              },
              {
                title: (
                  <span
                    className="submenu"
                    style={getSelectedStyle(params, "bestellungen")}
                    onClick={() => navigate(params, "bestellungen")}
                  >
                    {t("kundenbereich.menu.bestellungen")}
                  </span>
                ),
              },
              {
                title: (
                  <span
                    className="submenu"
                    style={getSelectedStyle(params, "kontrolltermine")}
                    onClick={() => navigate(params, "kontrolltermine")}
                  >
                    {t("kundenbereich.menu.kontrolltermine")}
                  </span>
                ),
              },
              {
                title: (
                  <span className="submenu" onClick={() => logout()}>
                    {t("kundenbereich.menu.logout")}
                  </span>
                ),
              },
            ]}
          />
        </div>
      );
    } else {
      setMenu(<></>);
    }
  }, [state, state.params]);

  // Redirect to login if not authenticated
  if (isLogoutPage()) {
    if (state.isAuthenticated) {
      return <>Sie konnten nicht abgemeldet werden. Bitte laden Sie die Seite neu.</>;
    }
    return <>Sie wurden abgemeldet.</>;
  }

  // Redirect to Resend Form if desired...
  if (isClientNumberPage()) {
    if (state.isAuthenticated) {
      return <>Sie sind bereits eingeloggt.</>;
    }
    return <EgwAuthenticationForm1Resend params={params} />;
  }

  if (!preload || state.isAuthenticated === null) {
    return <Spin />;
  }

  const HEADER = (
    <>
      {state.params?.h1 && <h1>{state.params.h1}</h1>}
      {state.params?.h2 && <h2>{state.params.h2}</h2>}
      {state.params?.h3 && <h3>{state.params.h3}</h3>}
    </>
  );

  if (state.isAuthenticated === false) {
    // Redirect to login if not authenticated
    if (isPageForbidden()) {
      return <Spin />;
    }

    // Show login form
    const error = isRedirectedLogin() || !isLoginPage() ? "Sie müssen sich einloggen, um diesen Teil der Webseite zu besuchen." : "";
    return (
      <>
        {error && (
          <>
            <Alert message={error} />
            <br />
          </>
        )}

        {HEADER}
        <EgwAuthenticationForm1Login params={params} />
      </>
    );
  }

  if (!state.user?.agb_accepted || !state.user?.data_protection) {
    const module = <EgwDisclaimerForm params={params} />;
    return (
      <>
        {menu}
        {HEADER}
        {module}
      </>
    );
  }

  let module = <>Seite nicht gefunden.</>;
  switch (params.section) {
    case "home":
      module = <EgwClientareaHome params={params} />;
      break;
    case "schlafqualitaet":
      module = <EgwClientareaSleepEvaluation params={params} />;
      break;
    case "befragungen":
      module = <EgwClientareaSurveys params={params} />;
      break;
    case "form":
      module = <EgwSurveyForm params={state.params} />;
      break;
    case "dokumente":
      module = <EgwClientareaDokumente params={params} />;
      break;
    case "bestellungen":
      module = <EgwClientareaBestellung params={params} />;
      break;
    case "kontrolltermine":
      module = <EgwKontrollterminRegistration params={params} />;
      break;
    case "login":
      // WAIT...
      module = <></>;
      break;
    case "unsubscribe":
      module = <EgwUnsubscribe params={params} />;
      break;
    case "hidden":
      module = <></>;
      break;
    default:
      break;
  }

  return (
    <>
      {/* {state.params?.showMenu && ( */}
      {menu}
      {HEADER}
      {module}
    </>
  );
};

export default EgwClientarea;
